/**
 * One Generate run as one list entry: the phone, the SKU, a thumbnail per view (its newest
 * job) and how many views are in each state. Opening the run shows its gallery.
 */
import { useEffect, useRef } from "react";
import type { GenerationJobView } from "@genz/platform";
import { RowAction } from "../ui/RowAction.js";
import { ConfirmPopover } from "../ui/ConfirmPopover.js";
import { inPassTwo, isReady } from "./candidates.js";
import { useGreyThumb } from "./useGreyThumb.js";
import { useStableUrl } from "./stableUrl.js";
import { latestPerView, phoneName, runCounts, runCountsText, viewLabel, type Run } from "./runs.js";

/** Never the magenta image: the clear image when there is one, a mock-up as it is, else the
 *  result with its print guide painted grey. `decorative` where the control around it names it. */
export function RunThumb({ job, testId, decorative, onImageError, onImageLoad }: {
  job: GenerationJobView; testId: string; decorative?: boolean;
  onImageError?: (id: string) => void; onImageLoad?: (id: string) => void;
}) {
  const hasClear = Boolean(job.clearUrl) && (job.status === "clear_review" || isReady(job) || inPassTwo(job));
  const [clear, dropClear] = useStableUrl(`${job.id}:clear:${job.clearRef ?? ""}`,
    hasClear ? job.clearThumbUrl ?? job.clearUrl : null);
  const result = job.thumbUrl ?? job.resultUrl;
  const guide = !hasClear && !job.mockupOnly && isReady(job) ? result : null;
  const grey = useGreyThumb(`${job.id}:${job.finishedAt ?? ""}`, guide, () => onImageError?.(job.id));
  const [mockup, dropMockup] = useStableUrl(`${job.id}:result:${job.finishedAt ?? ""}`,
    job.mockupOnly && isReady(job) ? result : null);
  const src = clear ?? mockup ?? grey;
  const failed = () => {
    dropClear();
    dropMockup();
    onImageError?.(job.id);
  };
  const name = viewLabel(job.view);
  const named = decorative ? { "aria-hidden": true } : { role: "img", "aria-label": name, title: name };
  return src ? (
    <img className="generate-panel__run-thumb" src={src} alt={decorative ? "" : name} title={decorative ? undefined : name}
      onError={failed} onLoad={() => onImageLoad?.(job.id)} data-testid={testId} />
  ) : (
    <span className={`generate-panel__run-thumb is-empty${job.status === "failed" || job.status === "held" ? " is-failed" : ""}`}
      {...named} data-testid={testId} />
  );
}

export interface RunRowProps {
  run: Run;
  i: number;
  onOpen: (run: Run) => void;
  onRemove?: (run: Run) => void;
  /** Bulk selection: the entry's checkbox, shown when `onSelect` is given. */
  selected?: boolean;
  onSelect?: (selected: boolean) => void;
  active?: boolean;
  /** A Remove request for this run is out. */
  busy?: boolean;
  /** A thumbnail's signed link did not load (they expire), or did. */
  onImageError?: (jobId: string) => void;
  onImageLoad?: (jobId: string) => void;
  /** Focus the open button: the person just came back from this run's gallery. */
  autoFocus?: boolean;
}

export function RunRow({
  run, i, onOpen, onRemove, selected, onSelect, active, busy, onImageError, onImageLoad, autoFocus,
}: RunRowProps) {
  const open = useRef<HTMLButtonElement>(null);
  useEffect(() => { if (autoFocus) open.current?.focus(); }, [autoFocus]);
  const views = latestPerView(run);
  const { name, brand, model } = run.details;
  const phone = phoneName(brand, model) || "Phone not named";
  const sku = name?.trim();
  const title = sku ? `${phone} ${sku}` : phone;
  const images = run.jobs.length;
  return (
    <li className={`generate-panel__run${active ? " is-active" : ""}`} data-testid={`run-row-${i}`} data-run={run.runId}>
      <div className="generate-panel__run-head">
        {onSelect && (
          <input type="checkbox" checked={!!selected} onChange={(e) => onSelect(e.target.checked)}
            aria-label={`Select ${title}`} data-testid={`run-select-${i}`} />
        )}
        <button ref={open} type="button" className="generate-panel__run-open" onClick={() => onOpen(run)}
          aria-label={`Open ${title} · ${runCountsText(runCounts(run))}`} data-testid={`run-open-${i}`}>
          <span className="generate-panel__run-thumbs">
            {views.map((job) => (
              <RunThumb key={job.id} job={job} testId={`run-thumb-${i}-${job.view}`} decorative
                onImageError={onImageError} onImageLoad={onImageLoad} />
            ))}
          </span>
          <span className="generate-panel__run-text">
            <span className="generate-panel__run-name">{phone}</span>
            {sku && <span className="caption muted" data-testid={`run-sku-${i}`}>{sku}</span>}
            <span className="caption" data-testid={`run-counts-${i}`}>{runCountsText(runCounts(run))}</span>
          </span>
        </button>
        {onRemove && (
          <ConfirmPopover
            message={images > 1
              ? `Remove this generation and its ${images} images? Running ones stop. The templates made from them stay.`
              : "Remove this generation? The template made from it stays."}
            confirmLabel="Remove" danger onConfirm={() => onRemove(run)}>
            {(toggle) => (
              <RowAction label={`Remove ${title}`} hint="Remove this generation" icon="minus-circle" tone="quiet"
                onClick={toggle} disabled={busy} busy={busy} data-testid={`run-remove-${i}`} />
            )}
          </ConfirmPopover>
        )}
      </div>
    </li>
  );
}
